import { useEffect, useState } from 'react'
import { Button, CircularProgress, Dialog, DialogTitle, Typography } from '@mui/material'
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import axios from 'axios'
import ReactGA from 'react-ga4'
import { useNavigate } from 'react-router-dom'
import { AppConstants } from 'AppConstants/AppConstants'
import useQueryParams from 'hooks/useQueryParams'
import { useAppSelector } from 'redux/store'
import { StatusEnum } from 'redux/types'
import { createNavigateWithQueryParams } from 'utils/navigateWithQueryParams'
import styles from './Payment.module.scss'

export function CheckoutFormSetup({ clientSecret, disableBackButton, enableBackButton }: any) {
  const { getAllParams } = useQueryParams()
  const stripe = useStripe()
  const elements = useElements()
  const navigate = useNavigate()
  // Create reusable navigation function
  const navigateWithQueryParams = createNavigateWithQueryParams(navigate, getAllParams)

  const [payment, setPayment] = useState('')
  const [buttonDisable, setButtonDisable] = useState(false)
  const [stripePaymentStatus, setStripePaymentStatus] = useState<StatusEnum>(StatusEnum.Idle)
  const selectedSlot = useAppSelector((state) => state.signUpPersist.selectedSlotSignUp)
  const [slotNotFound, setSlotNotFound] = useState(false)

  const [isPaymentElementFilled, setIsPaymentElementFilled] = useState(false)

  const handlePaymentElementChange = (event: any) => {
    setIsPaymentElementFilled(event.complete)
  }

  const schedulePhysicalVisit = async () => {
    if (selectedSlot?.scheduleObject) {
      const response: any = await axios
        .post(AppConstants.API_URL + '/visit/chiropractor/appointment', selectedSlot.scheduleObject, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
          },
        })
        .then((res) => {
          return res
        })
        .catch((err) => {
          return err.response
        })

      if (response?.data?.code === 'success') {
        // ReactGA.event('conversion', {
        //   send_to: process.env.REACT_APP_GA_CONVERSION_ID || 'AW-11337579902/40e2CN_xnqkZEP76lp4q',
        //   value: plan && plan[0] && plan[0]?.amount > 0 ? plan[0].amount / 100 : 0,
        //   currency: 'USD',
        // })
        navigateWithQueryParams('/signup/done')
      } else {
        if (response?.data?.code === 'slot_booked_already') {
          setSlotNotFound(true)
        } else if (response?.data?.code === 'slot_not_found') {
          setSlotNotFound(true)
        }
        return false
      }
    } else {
      return false
    }
  }

  const purchaseFreeCredit = async () => {
    try {
      const response: any = await axios
        .post(`${AppConstants.API_URL}/visit/chiropractor/free-appointment`, null, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('userToken')}`,
            'x-platform-id': process.env.REACT_APP_PLATFORM_ID || '',
          },
        })
        .then((res) => {
          console.log(res?.data.code, 'res?.data.code')
          if (res && res?.data.code === 'success') {
            schedulePhysicalVisit()
          }
        })
        .catch((err) => {
          if (err) {
            alert('Something went wrong. Please try again.')
          }
        })
    } catch (error) {
      console.error(error)
    }
  }

  const handlePayment = async (e: any) => {
    e.preventDefault()
    if (!stripe || !elements) {
      return
    }

    if (!buttonDisable) {
      setButtonDisable(true)
      let stripeValidation = await elements.submit()

      if (stripeValidation.error) {
        console.log(stripeValidation.error)
        setButtonDisable(false)
        return
      } else {
        setPayment('in_progress')
        disableBackButton()
      }

      stripe
        .confirmSetup({
          elements,
          clientSecret,
          redirect: 'if_required',
        })
        .then((response) => {
          if (response.setupIntent?.status === 'succeeded') {
            setStripePaymentStatus(StatusEnum.Success)
            setPayment('success')
            // Navigate to confirmation or next step after successful setup
            purchaseFreeCredit()
          } else {
            setStripePaymentStatus(StatusEnum.Failed)
            setPayment('')
            enableBackButton()
            alert('Payment method setup failed. Please try again')
          }
        })
        .catch((error) => {
          setStripePaymentStatus(StatusEnum.Failed)
          setPayment('')
          enableBackButton()
          alert('Payment method setup failed. Please try again')
        })
    }
  }

  useEffect(() => {
    ReactGA.event('page_view', {
      page_title: 'page_view',
      page_location: '/signup/payment-setup',
    })
  }, [])

  return (
    <>
      <form
        onSubmit={isPaymentElementFilled ? handlePayment : () => {}}
        style={{ flex: 1, position: 'relative', display: 'flex', flexDirection: 'column' }}
      >
        <div className={styles.paymentForm}>
          <PaymentElement
            onChange={handlePaymentElementChange}
            options={{
              layout: 'accordion',
              wallets: {
                applePay: 'auto',
                googlePay: 'auto',
              },
              paymentMethodOrder: ['apple_pay', 'google_pay', 'card'],
            }}
          />
        </div>

        {slotNotFound && (
          <Dialog
            onClose={() => {
              // setError(false)
            }}
            open={slotNotFound}
            PaperProps={{
              style: {
                background: 'var(--color-background)',
                borderRadius: '12px',
                padding: '10px 12px',
                minWidth: 300,
                maxWidth: 300,
                minHeight: 'auto',
                // height: 170,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                flex: 1, // align everything to the left
                textAlign: 'left', // global text alignment
              },
            }}
          >
            <DialogTitle
              style={{
                fontSize: '16px',
                fontWeight: 700,
                color: 'var(--color-text-primary)',
                textAlign: 'left',
                marginBottom: '12px',
                padding: 0,
                width: '100%',
              }}
            >
              Time Limit Reached
            </DialogTitle>
            <Typography
              style={{
                color: 'var(--color-text-primary)',
                fontSize: '14px',
                textAlign: 'left',
                marginBottom: '24px',
                lineHeight: 1.5,
                width: '100%',
              }}
            >
              Your booking session expired. Please select another available slot to continue.
            </Typography>
            <Button
              color="primary"
              style={{
                marginBottom: '0.3rem',
                height: '42px',
                padding: '0px 8px 0px 8px',
                fontSize: '14px',
                alignSelf: 'center',
              }}
              onClick={() => {
                setSlotNotFound(false)
                navigateWithQueryParams('/signup/slot')
              }}
            >
              Reschedule
            </Button>
          </Dialog>
        )}
        <div className={styles.footer}>
          <Button
            type={isPaymentElementFilled ? 'submit' : undefined}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              marginTop: '2rem',
              opacity: isPaymentElementFilled ? '1' : '0.5',
            }}
            style={
              payment === 'success'
                ? {
                    backgroundImage: "url('/images/doneImg.svg')",
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center center',
                  }
                : {}
            }
          >
            <span>{payment !== 'success' && payment !== 'in_progress' && 'Confirm Appointment'}</span>
            {payment === 'in_progress' && (
              <CircularProgress
                size={20}
                className={styles.progressBar}
                sx={{ color: 'white', width: '14px', height: '14px', left: '90%' }}
              />
            )}
          </Button>
        </div>
      </form>
    </>
  )
}
